import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _type_of } from "@swc/helpers/_/_type_of";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { HEADER_SUBDOMAIN_OVERRIDE } from "@faire/web--source/common/consts/HEADER_SUBDOMAIN_OVERRIDE";
import { replaceSubdomain } from "@faire/web--source/common/replaceSubdomain";
import { getLocationOrigin } from "@faire/web--source/common/splitHostname";
import { isError } from "@faire/web--source/common/typescriptUtils";
import { IApiError } from "@faire/web-api--source/indigofair/data/IApiError";
import { WebApiError } from "@faire/web-api--source/WebApiError";
import { WebApiNetworkError } from "@faire/web-api--source/WebApiNetworkError";
import { WebApiRequestCancellationError } from "@faire/web-api--source/WebApiRequestCancellationError";
import { parseJson } from "../../string";
import { prefetchReaderFactory } from "./__internal__/prefetchReaderFactory";
var HTML_TAG_REGEXP = /<\s*html\s*>/i;
export var fetchRequestHandlerFactory = function(customFetch) {
    return /*#__PURE__*/ function() {
        var _ref = _async_to_generator(function(requestOptions) {
            var _requestOptions_baseUrl, url, body, headers, includeCredentials, resp, credentials, requestInit, fetchFn, e, defaultError;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        url = new URL(requestOptions.url, (_requestOptions_baseUrl = requestOptions.baseUrl) !== null && _requestOptions_baseUrl !== void 0 ? _requestOptions_baseUrl : getLocationOrigin());
                        body = requestOptions.data;
                        headers = new Headers(requestOptions.headers);
                        if ((typeof body === "undefined" ? "undefined" : _type_of(body)) === "object" && headers.get("content-type") === "application/json") {
                            body = JSON.stringify(body);
                        }
                        if (body instanceof FormData && headers.get("content-type") === "multipart/form-data") {
                            // Clear the content-type when sending form data over fetch
                            // This stack overflow answer explains that the browser handles for us
                            // https://stackoverflow.com/questions/35192841/how-do-i-post-with-multipart-form-data-using-fetch
                            headers.delete("content-type");
                        }
                        includeCredentials = requestOptions.withCredentials;
                        if (requestOptions.subdomain && requestOptions.subdomain !== "www") {
                            url = new URL(url.href.replace(url.origin, ""), replaceSubdomain(url.origin, requestOptions.subdomain));
                            // This header is required by the local dev proxy to override the subdomain
                            headers.set(HEADER_SUBDOMAIN_OVERRIDE, requestOptions.subdomain);
                            includeCredentials = includeCredentials !== null && includeCredentials !== void 0 ? includeCredentials : true;
                        }
                        if (requestOptions.params) {
                            url.search = buildParams(url.search, requestOptions.params);
                        }
                        _state.label = 1;
                    case 1:
                        _state.trys.push([
                            1,
                            3,
                            ,
                            4
                        ]);
                        credentials = "omit";
                        if (includeCredentials === undefined) {
                            credentials = "same-origin";
                        } else if (includeCredentials) {
                            credentials = "include";
                        }
                        requestInit = {
                            method: requestOptions.method,
                            body: body,
                            headers: headers,
                            signal: requestOptions.signal,
                            credentials: credentials,
                            cache: requestOptions.cache,
                            redirect: requestOptions.redirect,
                            next: requestOptions.next
                        };
                        fetchFn = customFetch !== null && customFetch !== void 0 ? customFetch : fetch;
                        return [
                            4,
                            fetchFn(url.toString(), requestInit)
                        ];
                    case 2:
                        resp = _state.sent();
                        return [
                            3,
                            4
                        ];
                    case 3:
                        e = _state.sent();
                        if (isError(e) && e.name === "AbortError") {
                            return [
                                2,
                                {
                                    error: new WebApiRequestCancellationError()
                                }
                            ];
                        }
                        defaultError = new WebApiNetworkError("Network Error");
                        if (isError(e)) {
                            defaultError.name = e.name;
                            defaultError.message = "Network Error Fetching ".concat(requestOptions.url, ": ").concat(e.message);
                            defaultError.stack = e.stack;
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            defaultError.cause = e.cause;
                        }
                        return [
                            2,
                            {
                                error: defaultError
                            }
                        ];
                    case 4:
                        return [
                            2,
                            normalizeResponse(resp, requestOptions)
                        ];
                }
            });
        });
        return function(requestOptions) {
            return _ref.apply(this, arguments);
        };
    }();
};
/**
 * Normalizes the response from the fetch request to the expected WebApiResponse
 */ export var normalizeResponse = /*#__PURE__*/ function() {
    var _ref = _async_to_generator(function(resp, requestOptions) {
        var httpStatusCode, statusText, responseHeaders, validRedirect, isRawResponse, shouldError, _resp_headers_get, requestInfo, responseText, json, errorMessage, _tmp;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    httpStatusCode = resp.status;
                    statusText = resp.statusText;
                    responseHeaders = Object.fromEntries(resp.headers.entries());
                    validRedirect = requestOptions.redirect === "manual" && resp.status >= 300 && resp.status < 400;
                    isRawResponse = requestOptions.rawResponse === true;
                    shouldError = !isRawResponse && !validRedirect && !resp.ok;
                    if (!shouldError) return [
                        3,
                        2
                    ];
                    requestInfo = "".concat(requestOptions.method, " ").concat(resp.url.toString(), " ").concat(httpStatusCode, ": ").concat(statusText);
                    return [
                        4,
                        resp.text()
                    ];
                case 1:
                    responseText = _state.sent();
                    if ((_resp_headers_get = resp.headers.get("content-type")) === null || _resp_headers_get === void 0 ? void 0 : _resp_headers_get.startsWith("application/json")) {
                        try {
                            json = parseJson(responseText);
                        } catch (e) {
                            errorMessage = resp.headers.get("content-type") === "text/html" || HTML_TAG_REGEXP.test(responseText) ? undefined : responseText;
                            json = {
                                message: errorMessage
                            };
                        }
                    }
                    return [
                        2,
                        {
                            status: httpStatusCode,
                            headers: responseHeaders,
                            error: new WebApiError("".concat(requestInfo).concat((json === null || json === void 0 ? void 0 : json.message) ? ": ".concat(json.message) : ""), httpStatusCode, IApiError.build(_object_spread_props(_object_spread({}, json), {
                                status_code: httpStatusCode,
                                status_type: statusText
                            })))
                        }
                    ];
                case 2:
                    _tmp = {};
                    return [
                        4,
                        handleResponseType(resp, requestOptions)
                    ];
                case 3:
                    return [
                        2,
                        (_tmp.data = _state.sent(), _tmp.status = httpStatusCode, _tmp.headers = responseHeaders, _tmp)
                    ];
            }
        });
    });
    return function normalizeResponse(resp, requestOptions) {
        return _ref.apply(this, arguments);
    };
}();
var fetchHandler = fetchRequestHandlerFactory();
var handleResponseType = /*#__PURE__*/ function() {
    var _ref = _async_to_generator(function(resp, requestOptions) {
        var _resp_headers_get, data, _, text;
        return _ts_generator(this, function(_state) {
            switch(_state.label){
                case 0:
                    data = undefined;
                    if (!(requestOptions.responseType === "json")) return [
                        3,
                        2
                    ];
                    _ = JSON.parse;
                    return [
                        4,
                        resp.text()
                    ];
                case 1:
                    data = _.apply(JSON, [
                        _state.sent()
                    ]);
                    return [
                        3,
                        8
                    ];
                case 2:
                    if (!(requestOptions.responseType === "text")) return [
                        3,
                        4
                    ];
                    return [
                        4,
                        resp.text()
                    ];
                case 3:
                    data = _state.sent();
                    return [
                        3,
                        8
                    ];
                case 4:
                    if (!(requestOptions.responseType === "blob")) return [
                        3,
                        6
                    ];
                    return [
                        4,
                        resp.blob()
                    ];
                case 5:
                    data = _state.sent();
                    return [
                        3,
                        8
                    ];
                case 6:
                    if (!((_resp_headers_get = resp.headers.get("content-type")) === null || _resp_headers_get === void 0 ? void 0 : _resp_headers_get.startsWith("application/json"))) return [
                        3,
                        8
                    ];
                    return [
                        4,
                        resp.text()
                    ];
                case 7:
                    text = _state.sent();
                    try {
                        if (text.length > 0) {
                            data = JSON.parse(text);
                        } else {
                            // Empty string is not valid JSON, so we need to return an empty object
                            data = {};
                        }
                    } catch (e) {
                        // eslint-disable-next-line no-console
                        console.error("Failed to parse JSON: ".concat(e), {
                            response: {
                                status: resp.status,
                                statusText: resp.statusText,
                                method: requestOptions.method,
                                fetchTarget: resp.url
                            }
                        });
                        data = text;
                    }
                    _state.label = 8;
                case 8:
                    return [
                        2,
                        data
                    ];
            }
        });
    });
    return function handleResponseType(resp, requestOptions) {
        return _ref.apply(this, arguments);
    };
}();
var buildParams = function(existingSearch, params) {
    var search = new URLSearchParams(existingSearch);
    var _iteratorNormalCompletion = true, _didIteratorError = false, _iteratorError = undefined;
    try {
        for(var _iterator = Object.entries(params)[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true){
            var _step_value = _sliced_to_array(_step.value, 2), k = _step_value[0], v = _step_value[1];
            if (v === undefined) {
                continue;
            }
            var all = Array.isArray(v) ? v : [
                v
            ];
            var _iteratorNormalCompletion1 = true, _didIteratorError1 = false, _iteratorError1 = undefined;
            try {
                for(var _iterator1 = all[Symbol.iterator](), _step1; !(_iteratorNormalCompletion1 = (_step1 = _iterator1.next()).done); _iteratorNormalCompletion1 = true){
                    var each = _step1.value;
                    search.append(k, each);
                }
            } catch (err) {
                _didIteratorError1 = true;
                _iteratorError1 = err;
            } finally{
                try {
                    if (!_iteratorNormalCompletion1 && _iterator1.return != null) {
                        _iterator1.return();
                    }
                } finally{
                    if (_didIteratorError1) {
                        throw _iteratorError1;
                    }
                }
            }
        }
    } catch (err) {
        _didIteratorError = true;
        _iteratorError = err;
    } finally{
        try {
            if (!_iteratorNormalCompletion && _iterator.return != null) {
                _iterator.return();
            }
        } finally{
            if (_didIteratorError) {
                throw _iteratorError;
            }
        }
    }
    return search.toString();
};
export var requestHandler = prefetchReaderFactory(fetchHandler);
